import React, { useMemo, useState } from "react";

/** Aggregate counts for summary cards (uses same status semantics as MeetPage filters). */
export function computeMeetingSummaryStats(meetings, { effectiveMeetingStatus }) {
  const list = Array.isArray(meetings) ? meetings : [];
  let live = 0;
  let upcoming = 0;
  let completed = 0;
  for (const m of list) {
    const s = effectiveMeetingStatus(m);
    if (s === "live") live += 1;
    else if (s === "upcoming") upcoming += 1;
    else if (s === "completed" || s === "ended") completed += 1;
  }
  return {
    total: list.length,
    live,
    upcoming,
    completed,
  };
}

function SummaryCard({ tone, value, label, icon }) {
  return (
    <div className="avaada-summary-card">
      <div className={`avaada-summary-card__icon avaada-summary-card__icon--${tone}`}>{icon}</div>
      <div className="avaada-summary-card__body">
        <div className="avaada-summary-card__val">{value}</div>
        <div className="avaada-summary-card__label">{label}</div>
      </div>
    </div>
  );
}

export function MeetSummaryStatsRow({ stats, renderIcon }) {
  const s = stats || { total: 0, live: 0, upcoming: 0, completed: 0 };
  return (
    <div className="avaada-summary-grid" role="group" aria-label="Meeting summary">
      <SummaryCard
        tone="blue"
        value={s.total}
        label="Total Meetings"
        icon={renderIcon?.("video", 20) || null}
      />
      <SummaryCard
        tone="green"
        value={s.live}
        label="Live Now"
        icon={renderIcon?.("live", 20) || null}
      />
      <SummaryCard
        tone="amber"
        value={s.upcoming}
        label="Upcoming"
        icon={renderIcon?.("clock", 20) || null}
      />
      <SummaryCard
        tone="violet"
        value={s.completed}
        label="Completed"
        icon={renderIcon?.("check", 20) || null}
      />
    </div>
  );
}

export function MeetRedesignHero({
  title,
  subtitle,
  onScheduleMeeting,
  scheduleLabel = "Schedule Meeting",
  extraActions = null,
}) {
  return (
    <header className="avaada-meet-hero">
      <div className="avaada-meet-hero__text">
        <h1 className="avaada-meet-hero__title">{title}</h1>
        {subtitle ? <p className="avaada-meet-hero__sub">{subtitle}</p> : null}
      </div>
      <div className="avaada-meet-hero__actions">
        {extraActions}
        {onScheduleMeeting ? (
          <button type="button" className="avaada-btn-schedule" onClick={onScheduleMeeting}>
            <span className="ui-btn-ico" aria-hidden>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 4v16m8-8H4" />
              </svg>
            </span>
            {scheduleLabel}
          </button>
        ) : null}
      </div>
    </header>
  );
}

function parseTimeToMinutes(timeStr) {
  const raw = String(timeStr || "").trim().toLowerCase();
  if (!raw) return 9 * 60;
  const m = raw.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/i);
  if (!m) return 9 * 60;
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2] || "0", 10);
  const ap = (m[3] || "").toLowerCase();
  if (ap === "pm" && h < 12) h += 12;
  if (ap === "am" && h === 12) h = 0;
  return h * 60 + min;
}

const EVENT_COLORS = [
  { bg: "#eff6ff", border: "#bfdbfe" },
  { bg: "#ecfdf5", border: "#86efac" },
  { bg: "#fff7ed", border: "#fdba74" },
  { bg: "#f5f3ff", border: "#c4b5fd" },
];

export function UpcomingWeekPanel({
  meetings,
  weekFromYmd,
  weekToYmd,
  todayYmd,
  onOpen,
  meetingDateTimeMs,
  formatTimeLabel,
  renderPlatformIcon,
}) {
  const [pickedYmd, setPickedYmd] = useState(todayYmd);
  const dayMs = 86400000;

  const weekDays = useMemo(() => {
    const out = [];
    if (!weekFromYmd || !weekToYmd) return out;
    let cur = weekFromYmd;
    for (let i = 0; i < 7 && cur; i++) {
      const d = new Date(`${cur}T12:00:00`);
      out.push({
        ymd: cur,
        dow: d.toLocaleDateString(undefined, { weekday: "short" }),
        dom: d.getDate(),
      });
      const parts = cur.split("-").map(Number);
      const nd = new Date(parts[0], parts[1] - 1, parts[2] + 1);
      cur = `${nd.getFullYear()}-${String(nd.getMonth() + 1).padStart(2, "0")}-${String(nd.getDate()).padStart(2, "0")}`;
      if (cur > weekToYmd) break;
    }
    return out;
  }, [weekFromYmd, weekToYmd]);

  const dayMeetings = useMemo(() => {
    const list = Array.isArray(meetings) ? meetings : [];
    return list.filter((m) => String(m?.date || "").trim().slice(0, 10) === pickedYmd);
  }, [meetings, pickedYmd]);

  const timelineStart = 9 * 60;
  const timelineEnd = 18 * 60;
  const hourH = 48;
  const hours = [];
  for (let m = timelineStart; m <= timelineEnd; m += 60) hours.push(m);

  return (
    <div className="avaada-week-panel">
      <div className="avaada-week-strip" role="tablist" aria-label="Week days">
        {weekDays.map((d) => (
          <button
            key={d.ymd}
            type="button"
            role="tab"
            aria-selected={pickedYmd === d.ymd}
            className={`avaada-week-day${pickedYmd === d.ymd ? " is-selected" : ""}`}
            onClick={() => setPickedYmd(d.ymd)}
          >
            <span className="avaada-week-day__dow">{d.dow}</span>
            <span className="avaada-week-day__num">{d.dom}</span>
          </button>
        ))}
      </div>
      <div
        className="avaada-week-timeline"
        style={{ height: hours.length * hourH + 24, position: "relative" }}
      >
        {hours.map((min, i) => (
          <div
            key={min}
            className="avaada-week-timeline__hour"
            style={{ position: "absolute", top: i * hourH, left: 0, right: 0 }}
          >
            {formatHour(min)}
          </div>
        ))}
        {dayMeetings.map((m, idx) => {
          const startMin = parseTimeToMinutes(m?.time);
          const top = ((startMin - timelineStart) / 60) * hourH;
          if (top < 0 || top > hours.length * hourH) return null;
          const pal = EVENT_COLORS[idx % EVENT_COLORS.length];
          return (
            <button
              key={String(m?.id || m?.googleEventId || idx)}
              type="button"
              className="avaada-week-event"
              style={{
                top: top + 8,
                height: Math.min(72, hourH * 1.2),
                background: pal.bg,
                borderColor: pal.border,
              }}
              onClick={() => onOpen?.(m)}
            >
              <div className="avaada-week-event__title">{m?.title || "Meeting"}</div>
              <div className="avaada-week-event__meta">
                {formatTimeLabel?.(m) || m?.time || ""}
                {renderPlatformIcon ? ` · ${renderPlatformIcon(m)}` : ""}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function formatHour(minutes) {
  const h = Math.floor(minutes / 60);
  const suffix = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12} ${suffix}`;
}

export function UpcomingMeetingsRail({
  weekCount,
  attendedCount,
  totalEligible,
  nextMeetings,
  onOpen,
  onInstantJoin,
  onJoinUrl,
  inviteMeetAiToggle,
  formatTimeLabel,
}) {
  const pct = totalEligible > 0 ? Math.round((attendedCount / totalEligible) * 100) : 0;
  const [link, setLink] = useState("");

  return (
    <aside className="avaada-upcoming-rail">
      <div className="avaada-rail-card">
        <h3 className="avaada-rail-card__title">Meetings this week</h3>
        <div className="avaada-summary-card__val">{weekCount}</div>
        <div className="avaada-bar-chart" aria-hidden>
          {[40, 65, 55, 80, 70, 90, 60].map((h, i) => (
            <span key={i} style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>
      <div className="avaada-rail-card" style={{ textAlign: "center" }}>
        <h3 className="avaada-rail-card__title">Attendees</h3>
        <div className="avaada-attendance-ring" style={{ "--pct": `${pct * 3.6}deg` }}>
          <div className="avaada-attendance-ring__inner">{pct}%</div>
        </div>
        <div style={{ fontSize: 12, color: "#64748b" }}>
          {attendedCount} / {totalEligible || weekCount} meetings attended
        </div>
      </div>
      <div className="avaada-rail-card avaada-join-instant">
        <h3 className="avaada-rail-card__title">Join Instantly</h3>
        <input
          type="text"
          placeholder="Paste meeting link or code…"
          value={link}
          onChange={(e) => setLink(e.target.value)}
          aria-label="Meeting link or code"
        />
        <button
          type="button"
          onClick={() => {
            if (link.trim()) onJoinUrl?.(link.trim());
            else onInstantJoin?.();
          }}
        >
          Join Meeting
        </button>
      </div>
      <div className="avaada-rail-card">
        <h3 className="avaada-rail-card__title">Upcoming (Next 3)</h3>
        {(nextMeetings || []).slice(0, 3).map((m) => (
          <div key={String(m?.id || m?.googleEventId)} className="avaada-next-meeting-row">
            <div className="avaada-next-meeting-row__body">
              <div className="avaada-next-meeting-row__title">{m?.title || "Meeting"}</div>
              <div className="avaada-next-meeting-row__time">{formatTimeLabel?.(m) || m?.time}</div>
            </div>
            <button type="button" className="avaada-btn-join-sm" onClick={() => onOpen?.(m)}>
              Join
            </button>
          </div>
        ))}
      </div>
      {inviteMeetAiToggle ? (
        <div className="avaada-rail-card">{inviteMeetAiToggle}</div>
      ) : null}
    </aside>
  );
}

export function UpcomingMeetingsLayout({
  weekPanel,
  rail,
  children,
}) {
  return (
    <div className="avaada-upcoming-layout">
      <div className="avaada-upcoming-main">
        {weekPanel}
        <div className="avaada-meet-content-card">{children}</div>
      </div>
      {rail}
    </div>
  );
}

export function AllMeetingsLayout({ hero, summary, children }) {
  return (
    <div className="avaada-meet-page">
      {hero}
      {summary}
      <div className="avaada-meet-content-card">{children}</div>
    </div>
  );
}
